<template>
  <div class="chart-card">
    <div class="chart-card__header">
      <div class="chart-card__title-group">
        <h3 class="chart-card__title">{{ title }}</h3>
        <span v-if="subtitle" class="chart-card__subtitle">{{ subtitle }}</span>
      </div>
      <span v-if="totalValue" class="chart-card__total">{{ formattedTotal }}</span>
    </div>
    <div class="chart-card__body">
      <apexchart
        v-if="hasData"
        type="donut"
        :height="height"
        :options="chartOptions"
        :series="series"
      />
      <div v-else class="chart-card__empty">
        <font-awesome-icon icon="chart-pie" class="chart-card__empty-icon" />
        <p>Ingen data tilgjengelig</p>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'SalesDonutChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    title: {
      type: String,
      default: 'Salg per ordretype'
    },
    subtitle: {
      type: String,
      default: ''
    },
    labels: {
      type: Array,
      default: () => []
    },
    series: {
      type: Array,
      default: () => []
    },
    height: {
      type: Number,
      default: 280
    },
    colors: {
      type: Array,
      default: () => ['#B30E10', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4']
    }
  },
  computed: {
    hasData() {
      return this.series && this.series.length > 0 && this.series.some(v => v > 0);
    },
    totalValue() {
      return this.series.reduce((sum, val) => sum + val, 0);
    },
    formattedTotal() {
      return new Intl.NumberFormat('nb-NO').format(this.totalValue) + ' kr';
    },
    chartOptions() {
      return {
        chart: {
          type: 'donut',
          fontFamily: 'inherit',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        labels: this.labels,
        colors: this.colors,
        legend: {
          position: 'bottom',
          fontSize: '13px',
          fontWeight: 500,
          markers: {
            width: 12,
            height: 12,
            radius: 3
          },
          itemMargin: {
            horizontal: 12,
            vertical: 8
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--bo-muted)'
                },
                value: {
                  show: true,
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--bo-ink)',
                  formatter: (val) => {
                    return new Intl.NumberFormat('nb-NO').format(val) + ' kr';
                  }
                },
                total: {
                  show: true,
                  label: 'Totalt',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--bo-muted)',
                  formatter: (w) => {
                    const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                    return new Intl.NumberFormat('nb-NO').format(total) + ' kr';
                  }
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          width: 2,
          colors: ['var(--bo-surface)']
        },
        tooltip: {
          y: {
            formatter: (val) => {
              return new Intl.NumberFormat('nb-NO').format(val) + ' kr';
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              height: 250
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      };
    }
  }
};
</script>

<style scoped>
.chart-card {
  background: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.chart-card__title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chart-card__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0;
}

.chart-card__subtitle {
  font-size: 13px;
  font-weight: 400;
  color: var(--bo-muted);
}

.chart-card__total {
  font-size: 14px;
  font-weight: 600;
  color: var(--bo-alert-ink);
  background: var(--bo-bad-bg);
  padding: 4px 10px;
  border-radius: 6px;
}

.chart-card__body {
  min-height: 250px;
}

.chart-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 250px;
  color: var(--bo-muted-2);
}

.chart-card__empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}
</style>
